import {
  describeApiError,
  type ActivityRoomRetention,
  type ActivitySettings,
  type PutActivityRetentionRequest,
  type Room,
} from '@brush/api-client';
import { ErrorNotice } from '@brush/ui';
import { Archive } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  confirmationFor,
  effectiveRetention,
  refusalByField,
  retentionText,
  shortened,
  type RetentionField,
  type RetentionValues,
} from '../activity/settings';
import { usePutRetention, useRetentionImpact } from '../queries';

const inputClass =
  'w-28 rounded-lg border border-ink-800/15 px-3 py-2 text-sm text-ink-950 outline-none focus:border-felt-500';

const FIELDS: { field: RetentionField; key: keyof RetentionValues; label: string; hint: string }[] = [
  {
    field: 'audit',
    key: 'auditRetentionDays',
    label: 'Audit',
    hint: 'Who changed what — rooms, players, results, settings.',
  },
  {
    field: 'engagement',
    key: 'engagementRetentionDays',
    label: 'Usage',
    hint: 'Sessions and screens viewed. The monthly figures are kept regardless; only the raw events go.',
  },
];

/** A day count as typed: blank is "not set" (or, for a room, "follow the platform"). */
const parse = (text: string): number | null => (text.trim() === '' ? null : Number(text.trim()));
const show = (days: number | null) => (days === null ? '' : String(days));

/**
 * One retention form — the platform's, or a room's. Saving asks first whenever a class gets shorter,
 * with what the API counted the next purge would delete (step 26); a refusal is shown beside its field.
 */
function RetentionForm({
  roomId,
  current,
  minimumDays,
  blankMeans,
  platform,
  onDone,
}: {
  roomId: string | null;
  current: RetentionValues;
  minimumDays: number;
  /** For a room: the platform's figures, which a blank class follows. */
  platform?: RetentionValues;
  /** What an empty box means here, said beside it. */
  blankMeans: string;
  onDone?: () => void;
}) {
  const [draft, setDraft] = useState({
    auditRetentionDays: show(current.auditRetentionDays),
    engagementRetentionDays: show(current.engagementRetentionDays),
  });
  const [confirm, setConfirm] = useState<string | null>(null);
  const impact = useRetentionImpact();
  const put = usePutRetention();

  const proposed: RetentionValues = {
    auditRetentionDays: parse(draft.auditRetentionDays),
    engagementRetentionDays: parse(draft.engagementRetentionDays),
  };
  const invalid = Object.values(proposed).some((d) => d !== null && (!Number.isInteger(d) || d < 1));
  const changed =
    proposed.auditRetentionDays !== current.auditRetentionDays ||
    proposed.engagementRetentionDays !== current.engagementRetentionDays;
  const refusals = refusalByField(put.error, proposed, minimumDays);

  const body: PutActivityRetentionRequest = proposed;
  // A room is judged on what it would actually keep; the platform's own figures are what they say.
  const before = platform ? effectiveRetention(current, platform) : current;
  const after = platform ? effectiveRetention(proposed, platform) : proposed;
  const save = () =>
    put.mutate({ roomId, body }, { onSuccess: () => { setConfirm(null); onDone?.(); } });

  const onSave = () => {
    put.reset();
    if (shortened(before, after).length === 0) return save();
    // Shorter: count what the next purge would delete — nothing is deleted to count it — and ask.
    impact.mutate({ roomId, body }, {
      onSuccess: (counted) => {
        const ask = confirmationFor(before, after, counted);
        if (ask.required) setConfirm(ask.message);
        else save();
      },
    });
  };

  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-2">
        {FIELDS.map(({ field, key, label, hint }) => {
          const stored = retentionText(current[key]);
          return (
            <label key={field} className="block">
              <span className="block text-sm font-semibold text-ink-950">{label}</span>
              <span className="block text-xs text-ink-800/60">{hint}</span>
              <span className="mt-1 block text-xs text-ink-800/70">
                Now: <strong>{stored.figure}</strong>
                {stored.note && roomId === null ? <> — {stored.note}</> : null}
              </span>
              <span className="mt-2 flex items-center gap-2">
                <input
                  inputMode="numeric"
                  value={draft[key]}
                  disabled={confirm !== null || put.isPending}
                  onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                  className={inputClass}
                  aria-invalid={refusals[field] ? true : undefined}
                />
                <span className="text-xs text-ink-800/60">days · blank {blankMeans}</span>
              </span>
              {refusals[field] ? <span className="mt-1 block text-xs text-red-700">{refusals[field]}</span> : null}
            </label>
          );
        })}
      </div>

      <ErrorNotice message={refusals.form ?? describeApiError(impact.error)} />

      {confirm ? (
        <div role="alertdialog" aria-label="Confirm a shorter retention" className="rounded-lg border border-red-700/30 bg-red-50 p-3">
          <p className="text-sm text-ink-950">{confirm}</p>
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={save} disabled={put.isPending}
              className="rounded-lg bg-red-700 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60">
              Shorten retention
            </button>
            <button type="button" onClick={() => setConfirm(null)} disabled={put.isPending}
              className="rounded-lg border border-ink-800/15 px-3 py-1.5 text-sm text-ink-800">
              Keep it as it is
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={onSave} disabled={!changed || invalid || put.isPending || impact.isPending}
          className="rounded-lg bg-felt-700 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">
          {impact.isPending ? 'Counting…' : 'Save retention'}
        </button>
      )}
    </div>
  );
}

/** Platform retention, and the rooms that keep their own (ADR 0012 Decision 11). */
export function ActivityRetentionPanel({ settings, rooms }: { settings: ActivitySettings; rooms: readonly Room[] }) {
  const [adding, setAdding] = useState<string>('');
  const roomName = useMemo(() => new Map(rooms.map((r) => [r.id, r.name])), [rooms]);
  const own = new Set(settings.rooms.map((r) => r.roomId));
  const minimum = settings.platform.minimumDays;
  const none: RetentionValues = { auditRetentionDays: null, engagementRetentionDays: null };

  return (
    <section className="overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
      <header className="border-b border-ink-800/10 px-6 py-4">
        <h2 className="flex items-center gap-2 font-display font-semibold text-ink-950">
          <Archive className="size-4 text-ink-800/60" /> Activity retention
        </h2>
        <p className="mt-1 text-xs text-ink-800/60">
          How long raw activity is kept before the purge deletes it. At least {minimum} days each. A shorter
          period is permanent once the purge runs, so saving one asks first.
        </p>
      </header>

      <div className="space-y-6 p-6">
        <RetentionForm
          key={`platform-${show(settings.platform.auditRetentionDays)}-${show(settings.platform.engagementRetentionDays)}`}
          roomId={null}
          current={settings.platform}
          minimumDays={minimum}
          blankMeans="leaves it not set"
        />

        <div>
          <h3 className="text-sm font-semibold text-ink-950">Rooms keeping their own</h3>
          <p className="text-xs text-ink-800/60">
            A room may keep its history longer than the platform, never shorter than the audit floor.
          </p>
          <ul className="mt-3 space-y-4">
            {settings.rooms.map((r: ActivityRoomRetention) => (
              <li key={r.roomId} className="rounded-lg border border-ink-800/10 p-3">
                <div className="mb-2 text-sm font-semibold text-ink-950">
                  {roomName.get(r.roomId) ?? 'A room that no longer exists'}
                </div>
                <RetentionForm
                  key={`${r.roomId}-${show(r.auditRetentionDays)}-${show(r.engagementRetentionDays)}`}
                  roomId={r.roomId}
                  current={r}
                  minimumDays={minimum}
                  blankMeans="follows the platform"
                  platform={settings.platform}
                />
              </li>
            ))}
          </ul>

          <div className="mt-3 rounded-lg border border-dashed border-ink-800/15 p-3">
            <label className="flex items-center gap-2 text-sm text-ink-800">
              Give a room its own retention:
              <select value={adding} onChange={(e) => setAdding(e.target.value)}
                className="rounded-lg border border-ink-800/15 px-2 py-1.5 text-sm">
                <option value="">Choose a room…</option>
                {rooms.filter((r) => !own.has(r.id)).map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </label>
            {adding ? (
              <div className="mt-3">
                <RetentionForm key={adding} roomId={adding} current={none} minimumDays={minimum}
                  blankMeans="follows the platform" platform={settings.platform} onDone={() => setAdding('')} />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
